import React, { useEffect, useState } from 'react';
import { Button, Divider, Input, Modal, Select, TimePicker, Typography } from 'antd';
import dayjs from 'dayjs';

const { Text } = Typography;

const ALLOWANCE_TYPES = [
  { label: 'Meal', value: 'Meal' },
  { label: 'Trip', value: 'Trip' },
  { label: 'Sleepover', value: 'Sleepover' },
];

const toTimeValue = (value) => {
  if (!value) {
    return null;
  }

  return dayjs(value, 'HH:mm');
};

const ShiftActionModal = ({
  open,
  selectedBookedItem,
  onClose,
  onStartShift,
  onEndShift,
  onOpenCantWork,
  onRemove,
  onSaveBreaks,
  onSaveAllowances,
}) => {
  const status = selectedBookedItem?.status || '';
  const hasEnded = status === 'timesheet';
  const hasStarted = status === 'sstarted' || hasEnded;
  const [breakRows, setBreakRows] = useState([]);
  const [allowanceRows, setAllowanceRows] = useState([]);

  useEffect(() => {
    setBreakRows(Array.isArray(selectedBookedItem?.breaks) ? selectedBookedItem.breaks : []);
    setAllowanceRows(Array.isArray(selectedBookedItem?.timeAllocationAllowances) ? selectedBookedItem.timeAllocationAllowances : []);
  }, [selectedBookedItem]);

  const addBreakRow = () => {
    setBreakRows((current) => [...current, { starttime: '', endtime: '' }]);
  };

  const updateBreakRow = (index, field, value) => {
    setBreakRows((current) => current.map((row, rowIndex) => {
      if (rowIndex !== index) {
        return row;
      }

      return {
        ...row,
        [field]: value ? value.format('HH:mm') : '',
      };
    }));
  };

  const removeBreakRow = (index) => {
    setBreakRows((current) => current.filter((_, rowIndex) => rowIndex !== index));
  };

  const addAllowanceRow = () => {
    setAllowanceRows((current) => [...current, { allowanceType: '', unit: '', totalAmount: '' }]);
  };

  const updateAllowanceRow = (index, field, value) => {
    setAllowanceRows((current) => current.map((row, rowIndex) => {
      if (rowIndex !== index) {
        return row;
      }

      return {
        ...row,
        [field]: value,
      };
    }));
  };

  const removeAllowanceRow = (index) => {
    setAllowanceRows((current) => current.filter((_, rowIndex) => rowIndex !== index));
  };

  return (
    <Modal open={open} onCancel={onClose} footer={null} title="Shift actions" width={760}>
      <div style={{ display: 'grid', gap: 12 }}>
        <Text strong>{selectedBookedItem?.date || '-'}</Text>
        <Text>
          {selectedBookedItem?.starttime || '-'} - {selectedBookedItem?.endtime || '-'}
        </Text>
        {selectedBookedItem?.areaName ? <Text>Area: {selectedBookedItem.areaName}</Text> : null}
        <div style={{ display: 'grid', gap: 8 }}>
          <Button type="primary" onClick={onStartShift} disabled={hasStarted}>Start shift</Button>
          <Button onClick={onEndShift} disabled={!hasStarted || hasEnded}>End shift</Button>
          <Button onClick={onOpenCantWork} disabled={hasEnded}>Can't work</Button>
         
        </div>

        <Divider style={{ margin: '4px 0' }}>Breaks</Divider>
        <div style={{ display: 'grid', gap: 8 }}>
          {breakRows.map((breakItem, index) => (
            <div key={`break-${index}`} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <TimePicker
                value={toTimeValue(breakItem.starttime)}
                onChange={(value) => updateBreakRow(index, 'starttime', value)}
                format="HH:mm"
              />
              <TimePicker
                value={toTimeValue(breakItem.endtime)}
                onChange={(value) => updateBreakRow(index, 'endtime', value)}
                format="HH:mm"
              />
              <Button onClick={() => removeBreakRow(index)}>Remove</Button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={addBreakRow}>Add break</Button>
            <Button
              type="primary"
              onClick={() => onSaveBreaks({
                ...selectedBookedItem,
                breaks: breakRows.filter((item) => item.starttime && item.endtime),
              })}
            >
              Save breaks
            </Button>
          </div>
        </div>

        <Divider style={{ margin: '4px 0' }}>Allowances</Divider>
        <div style={{ display: 'grid', gap: 8 }}>
          {allowanceRows.map((allowance, index) => (
            <div key={`allowance-${index}`} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr auto', gap: 8, alignItems: 'center' }}>
              <Select
                value={allowance.allowanceType || undefined}
                options={ALLOWANCE_TYPES}
                placeholder="Allowance type"
                onChange={(value) => updateAllowanceRow(index, 'allowanceType', value)}
              />
              <Input
                value={allowance.unit}
                placeholder="Unit"
                onChange={(event) => updateAllowanceRow(index, 'unit', event.target.value)}
              />
              <Input
                value={allowance.totalAmount}
                placeholder="Total"
                onChange={(event) => updateAllowanceRow(index, 'totalAmount', event.target.value)}
              />
              <Button onClick={() => removeAllowanceRow(index)}>Remove</Button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={addAllowanceRow}>Add allowance</Button>
            <Button
              type="primary"
              onClick={() => onSaveAllowances({
                ...selectedBookedItem,
                timeAllocationAllowances: allowanceRows.filter((item) => item.allowanceType),
              })}
            >
              Save allowances
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ShiftActionModal;